import React, { useEffect, useMemo, useState, useCallback } from "react";
import {
    getExpirySummary,
    getUpcomingPointExpiry,
    getExpiredPoints,
    getOverduePoints,
    downloadUpcomingPointExpiry,
    downloadExpiredPoints,
    downloadOverduePoints,
} from "../../service/query/auth";

const BAND_STYLES = {
    EXPIRED: "bg-red-50 text-red-700 ring-red-200",
    OVERDUE: "bg-orange-50 text-orange-700 ring-orange-200",
    DEFAULT: "bg-amber-50 text-amber-700 ring-amber-200",
};

const bandLabel = (band) => {
    if (band === "EXPIRED" || band === "OVERDUE") return band;
    return `${band} days`;
};

const BAND_DESCRIPTIONS = {
    EXPIRED: "Points that have already passed their expiry date and are no longer valid.",
    OVERDUE: "Points that are overdue and should have been processed.",
    "5": "Points expiring within the next 5 days.",
    "30": "Points expiring within the next 30 days.",
    "60": "Points expiring within the next 60 days.",
    "90": "Points expiring within the next 90 days.",
};

const getTransactionTypeName = (typeId) => {
    const numericId = Number(typeId);
    if (numericId === 1) return "Point Acquisition";
    if (numericId === 3) return "Transfer In";
    return typeId ?? "-";
};

const SummaryStrip = ({ summary, loading, error, onRetry }) => {
    const [hoveredBand, setHoveredBand] = useState(null);

    if (loading) {
        return (
            <div className="grid grid-cols-2 gap-3 sm:grid-cols-4 lg:grid-cols-7 mb-6">
                {Array.from({ length: 7 }).map((_, i) => (
                    <div key={i} className="h-16 rounded-xl bg-gray-100 animate-pulse" />
                ))}
            </div>
        );
    }

    if (error) {
        return (
            <div className="mb-6 p-4 rounded-xl bg-amber-50 border border-amber-200 text-amber-800 text-xs flex items-center justify-between">
                <span>Failed to load summary statistics.</span>
                <button
                    onClick={onRetry}
                    className="px-3 py-1 bg-amber-600 text-white font-medium rounded-lg hover:bg-amber-700 transition-colors"
                >
                    Retry
                </button>
            </div>
        );
    }

    if (!summary?.length) return null;

    return (
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-4 lg:grid-cols-7 mb-6">
            {summary.map((band) => (
                <div
                    key={band.band}
                    className={`rounded-xl ring-1 p-3 relative cursor-default ${BAND_STYLES[band.band] || BAND_STYLES.DEFAULT
                        }`}
                    onMouseEnter={() => setHoveredBand(band.band)}
                    onMouseLeave={() => setHoveredBand(null)}
                >
                    <p className="text-xs font-medium uppercase tracking-wide opacity-80">
                        {bandLabel(band.band)}
                    </p>
                    <p className="text-lg font-semibold mt-1">
                        {Number(band.points_at_risk || 0).toLocaleString()} pts
                    </p>
                    {hoveredBand === band.band && (
                        <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 w-[180px] bg-[#212121] text-white text-[11px] rounded-lg px-3 py-2 z-10 shadow-lg">
                            <p>
                                {BAND_DESCRIPTIONS[band.band] ?? "Points at risk in this band."}
                            </p>
                            <p className="mt-1 opacity-70">
                                {band.members} member {band.members === 1 ? "" : "s"} · {band.lots} lot {band.lots === 1 ? "" : "s"}
                            </p>
                            <div className="absolute top-full left-1/2 -translate-x-1/2 border-4 border-transparent border-t-[#212121]" />
                        </div>
                    )}
                </div>
            ))}
        </div>
    );
};

const Field = ({ label, required, disabled, children }) => (
    <div className={`transition-opacity duration-200 ${disabled ? "opacity-40" : "opacity-100"}`}>
        <label className="block text-sm text-gray-700 mb-1">
            {label} {required && !disabled && <span className="text-red-500">*</span>}
        </label>
        {children}
    </div>
);

const inputClass =
    "w-full rounded-lg border border-gray-200 px-3 py-2 text-sm text-gray-700 focus:outline-none focus:ring-2 focus:ring-blue-100 focus:border-blue-400 disabled:bg-gray-100 disabled:text-gray-400 disabled:cursor-not-allowed";

const PAGE_SIZE_OPTIONS = [10, 20, 50];

const downloadJsonAsCsv = (data, filename, customHeadersMap = null) => {
    if (!Array.isArray(data) || data.length === 0) {
        return false;
    }

    const rawKeys = Object.keys(data[0]);
    const headers = customHeadersMap
        ? rawKeys.map((key) => customHeadersMap[key] || key)
        : rawKeys;

    const escapeCsvValue = (value) => {
        if (value === null || value === undefined) return "";
        const stringValue = String(value);
        if (stringValue.includes(",") || stringValue.includes('"') || stringValue.includes("\n")) {
            return `"${stringValue.replace(/"/g, '""')}"`;
        }
        return stringValue;
    };

    const csvRows = [
        headers.map(escapeCsvValue).join(","),
        ...data.map((row) =>
            rawKeys.map((key) => {
                let val = row[key];
                if (key === "Transaction_type_id") {
                    val = getTransactionTypeName(val);
                }
                return escapeCsvValue(val);
            }).join(",")
        ),
    ];

    const csvContent = csvRows.join("\n");
    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    a.remove();
    window.URL.revokeObjectURL(url);
    return true;
};

export default function PointsExpiry() {
    const [pointsType, setPointsType] = useState("expired"); // "expired" | "upcoming" | "overdue"
    const [expiryDays, setExpiryDays] = useState(30);
    const [fromDate, setFromDate] = useState("");
    const [tillDate, setTillDate] = useState("");
    const [membershipId, setMembershipId] = useState("");
    const [appliedFilters, setAppliedFilters] = useState(null);

    const [summary, setSummary] = useState([]);
    const [summaryLoading, setSummaryLoading] = useState(true);
    const [summaryError, setSummaryError] = useState(false);

    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [downloading, setDownloading] = useState(false);

    const [page, setPage] = useState(1);
    const [perPage, setPerPage] = useState(10);
    const [totalPages, setTotalPages] = useState(1);
    const [totalRows, setTotalRows] = useState(0);

    const fetchSummary = useCallback(() => {
        setSummaryLoading(true);
        setSummaryError(false);
        getExpirySummary()
            .then((res) => {
                setSummary(res?.data?.data ?? []);
            })
            .catch(() => {
                setSummaryError(true);
                setSummary([]);
            })
            .finally(() => {
                setSummaryLoading(false);
            });
    }, []);

    useEffect(() => {
        fetchSummary();
    }, [fetchSummary]);

    const handlePointTypeChange = (type) => {
        setPointsType(type);
        setPage(1);
        setError(null);
        // Clear dates if not selecting expired
        if (type !== "expired") {
            setFromDate("");
            setTillDate("");
        }
    };

    const fetchRows = useCallback(
        async (filters, targetPage) => {
            setLoading(true);
            setError(null);
            try {
                const payload = {
                    fromDate: pointsType === "expired" ? (filters?.fromDate || undefined) : undefined,
                    tillDate: pointsType === "expired" ? (filters?.tillDate || undefined) : undefined,
                    membershipId: filters?.membershipId || undefined,
                    days: pointsType === "upcoming" ? filters?.days : undefined,
                    limit: perPage,
                    page: targetPage,
                };

                let res;
                if (pointsType === "expired") {
                    res = await getExpiredPoints(payload);
                } else if (pointsType === "upcoming") {
                    res = await getUpcomingPointExpiry(payload);
                } else {
                    res = await getOverduePoints(payload);
                }

                const data = res?.data?.data ?? [];
                const pagination = res?.data?.pagination;

                setRows(data);

                if (pagination && typeof pagination === "object") {
                    setTotalPages(pagination.total_pages || 1);
                    setTotalRows(pagination.total ?? data.length);
                } else {
                    setTotalPages(1);
                    setTotalRows(data.length);
                }
            } catch (err) {
                setError(
                    err?.response?.data?.message || "Unable to retrieve point records right now."
                );
                setRows([]);
                setTotalPages(1);
                setTotalRows(0);
            } finally {
                setLoading(false);
            }
        },
        [pointsType, perPage]
    );

    useEffect(() => {
        if (appliedFilters === null) return;
        fetchRows(appliedFilters, page);
    }, [appliedFilters, page, perPage, pointsType, fetchRows]);

    const handleSearch = () => {
        setError(null);

        if (pointsType === "expired" && (!fromDate || !tillDate)) {
            setError("Please fill in both From Date and Till Date.");
            return;
        }

        setPage(1);
        setAppliedFilters({
            fromDate: pointsType === "expired" ? fromDate : undefined,
            tillDate: pointsType === "expired" ? tillDate : undefined,
            membershipId,
            days: expiryDays,
        });
    };

    const handleDownload = async () => {
        setError(null);

        if (pointsType === "expired" && (!fromDate || !tillDate)) {
            setError("Please fill in both From Date and Till Date before downloading.");
            return;
        }

        setDownloading(true);
        try {
            const payload = {
                membershipId: membershipId || undefined,
                ...(pointsType === "expired" && {
                    fromDate: fromDate || undefined,
                    tillDate: tillDate || undefined,
                }),
                ...(pointsType === "upcoming" && {
                    days: expiryDays,
                }),
            };

            let res;
            let filename = "";
            let headerMap = {};

            if (pointsType === "expired") {
                res = await downloadExpiredPoints(payload);
                filename = `expired-points.csv`;
                headerMap = {
                    Membership1_id: "Membership ID",
                    transactionRef: "Transaction Ref",
                    Transaction_type_id: "Transaction Type",
                    Transaction_date: "Points Issuance Date",
                    Update_date: "Expiry Date",
                    points_expired: "Forfeited Points",
                };
            } else if (pointsType === "upcoming") {
                res = await downloadUpcomingPointExpiry(payload);
                filename = `upcoming-expiry-${expiryDays}-days.csv`;
                headerMap = {
                    Membership1_id: "Membership ID",
                    transactionRef: "Transaction Ref",
                    Transaction_type_id: "Transaction Type",
                    Transaction_channel_code: "Channel",
                    Transaction_date: "Points Issuance Date",
                    points_to_expire: "Points to Expire",
                    days_remaining: "Days Remaining",
                };
            } else {
                res = await downloadOverduePoints(payload);
                filename = `overdue-points.csv`;
                headerMap = {
                    Membership1_id: "Membership ID",
                    transactionRef: "Transaction Ref",
                    Transaction_type_id: "Transaction Type",
                    Transaction_channel_code: "Channel",
                    Transaction_date: "Issuance Date",
                    due_date: "Due Date",
                    points_overdue: "Points Overdue",
                    days_overdue: "Days Overdue",
                };
            }

            const data = res?.data?.data ?? [];

            if (!data.length) {
                setError("No records available for download with current criteria.");
            } else {
                downloadJsonAsCsv(data, filename, headerMap);
            }
        } catch (err) {
            setError(
                err?.response?.data?.message || "Unable to download report. Please try again."
            );
        } finally {
            setDownloading(false);
        }
    };

    const columns = useMemo(() => {
        if (pointsType === "expired") {
            return [
                { key: "Membership1_id", label: "Membership ID" },
                { key: "transactionRef", label: "Transaction Ref" },
                {
                    key: "Transaction_type_id",
                    label: "Transaction Type",
                    render: (val) => getTransactionTypeName(val),
                },
                { key: "Transaction_date", label: "Points Issuance Date" },
                { key: "Update_date", label: "Expiry Date" },
                {
                    key: "points_expired",
                    label: "Forfeited Points",
                    align: "right",
                },
            ];
        }

        if (pointsType === "overdue") {
            return [
                { key: "Membership1_id", label: "Membership ID" },
                { key: "transactionRef", label: "Transaction Ref" },
                {
                    key: "Transaction_type_id",
                    label: "Transaction Type",
                    render: (val) => getTransactionTypeName(val),
                },
                { key: "Transaction_channel_code", label: "Channel" },
                { key: "Transaction_date", label: "Issuance Date" },
                { key: "due_date", label: "Due Date" },
                {
                    key: "points_overdue",
                    label: "Points Overdue",
                    align: "right",
                },
                {
                    key: "days_overdue",
                    label: "Days Overdue",
                    align: "right",
                },
            ];
        }

        return [
            { key: "Membership1_id", label: "Membership ID" },
            { key: "transactionRef", label: "Transaction Ref" },
            {
                key: "Transaction_type_id",
                label: "Transaction Type",
                render: (val) => getTransactionTypeName(val),
            },
            { key: "Transaction_channel_code", label: "Channel" },
            { key: "Transaction_date", label: "Points Issuance Date" },
            {
                key: "points_to_expire",
                label: "Points to Expire",
                align: "right",
            },
            {
                key: "days_remaining",
                label: "Days Remaining",
                align: "right",
            },
        ];
    }, [pointsType]);

    // Conditionals for field activity states
    const isDateActive = pointsType === "expired";
    const isExpiryDaysActive = pointsType === "upcoming";

    return (
        <div className="p-6">
            <div className="bg-white rounded-xl shadow-sm p-6">
                <div className="mb-6">
                    <h2 className="text-base font-semibold text-gray-800">
                        Points Expiry & Backlog
                    </h2>
                    <p className="text-sm text-gray-500">
                        View upcoming, expired, and overdue backlog points
                    </p>
                </div>

                <SummaryStrip
                    summary={summary}
                    loading={summaryLoading}
                    error={summaryError}
                    onRetry={fetchSummary}
                />

                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4 mb-4">
                    <Field label="From Date" required={isDateActive} disabled={!isDateActive}>
                        <input
                            type="date"
                            className={inputClass}
                            value={fromDate}
                            onChange={(e) => setFromDate(e.target.value)}
                            disabled={!isDateActive}
                        />
                    </Field>
                    <Field label="Till Date" required={isDateActive} disabled={!isDateActive}>
                        <input
                            type="date"
                            className={inputClass}
                            value={tillDate}
                            onChange={(e) => setTillDate(e.target.value)}
                            disabled={!isDateActive}
                        />
                    </Field>
                    <Field label="Membership ID">
                        <input
                            type="text"
                            placeholder="Insert membership ID"
                            className={inputClass}
                            value={membershipId}
                            onChange={(e) => setMembershipId(e.target.value)}
                        />
                    </Field>
                    <Field label="Expiry Period" disabled={!isExpiryDaysActive}>
                        <select
                            className={inputClass}
                            value={expiryDays}
                            onChange={(e) => setExpiryDays(Number(e.target.value))}
                            disabled={!isExpiryDaysActive}
                        >
                            <option value={30}>30 days</option>
                            <option value={60}>60 days</option>
                            <option value={90}>90 days</option>
                        </select>
                    </Field>
                    <Field label="Rows per page">
                        <select
                            className={inputClass}
                            value={perPage}
                            onChange={(e) => {
                                setPerPage(Number(e.target.value));
                                setPage(1);
                            }}
                        >
                            {PAGE_SIZE_OPTIONS.map((n) => (
                                <option key={n} value={n}>
                                    {n} / page
                                </option>
                            ))}
                        </select>
                    </Field>
                </div>

                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
                    <div>
                        <p className="text-sm text-gray-700 mb-2 font-medium">Points type</p>
                        <div className="flex items-center gap-6">
                            <label className="flex items-center gap-2 text-sm text-gray-700 cursor-pointer">
                                <input
                                    type="radio"
                                    name="pointsType"
                                    checked={pointsType === "expired"}
                                    onChange={() => handlePointTypeChange("expired")}
                                />
                                Expired
                            </label>
                            <label className="flex items-center gap-2 text-sm text-gray-700 cursor-pointer">
                                <input
                                    type="radio"
                                    name="pointsType"
                                    checked={pointsType === "upcoming"}
                                    onChange={() => handlePointTypeChange("upcoming")}
                                />
                                To be expired
                            </label>
                            <label className="flex items-center gap-2 text-sm text-gray-700 cursor-pointer">
                                <input
                                    type="radio"
                                    name="pointsType"
                                    checked={pointsType === "overdue"}
                                    onChange={() => handlePointTypeChange("overdue")}
                                />
                                Overdue
                            </label>
                        </div>
                    </div>
                    <div className="flex gap-3">
                        <button
                            onClick={handleSearch}
                            className="px-6 py-2 rounded-lg bg-[#1c3f60] text-white text-sm font-medium hover:bg-[#163150] transition-colors"
                        >
                            Search
                        </button>
                        <button
                            onClick={handleDownload}
                            disabled={downloading}
                            className="px-6 py-2 rounded-lg border border-[#1c3f60] text-[#1c3f60] text-sm font-medium hover:bg-blue-50 transition-colors disabled:opacity-50"
                        >
                            {downloading ? "Preparing..." : "Download"}
                        </button>
                    </div>
                </div>

                {error && (
                    <div className="mb-4 rounded-lg bg-red-50 border border-red-200 text-red-700 text-sm px-4 py-3 flex items-center justify-between">
                        <span>{error}</span>
                        {appliedFilters && (
                            <button
                                onClick={() => fetchRows(appliedFilters, page)}
                                className="text-xs bg-red-600 text-white px-2.5 py-1 rounded hover:bg-red-700 transition-colors"
                            >
                                Retry Search
                            </button>
                        )}
                    </div>
                )}

                {appliedFilters === null && (
                    <div className="bg-[#F5F5F5] h-[200px] flex items-center justify-center rounded-lg mt-4">
                        <p className="text-[#6D6D6D] text-[14px]">
                            Select filters and click Search to see results
                        </p>
                    </div>
                )}

                {appliedFilters !== null && (
                    <div className="overflow-x-auto rounded-lg border border-gray-100">
                        <table className="min-w-full text-sm">
                            <thead>
                                <tr className="bg-gray-50 text-left text-gray-500">
                                    {columns.map((col) => (
                                        <th
                                            key={col.key}
                                            className={`px-4 py-3 font-medium whitespace-nowrap ${col.align === "right" ? "text-right" : "text-left"
                                                }`}
                                        >
                                            {col.label}
                                        </th>
                                    ))}
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-100">
                                {loading ? (
                                    Array.from({ length: 5 }).map((_, i) => (
                                        <tr key={i}>
                                            {columns.map((col) => (
                                                <td key={col.key} className="px-4 py-3">
                                                    <div className="h-4 w-24 bg-gray-100 rounded animate-pulse" />
                                                </td>
                                            ))}
                                        </tr>
                                    ))
                                ) : rows.length === 0 ? (
                                    <tr>
                                        <td
                                            colSpan={columns.length}
                                            className="px-4 py-8 text-center text-gray-400"
                                        >
                                            No records found matching these parameters.
                                        </td>
                                    </tr>
                                ) : (
                                    rows.map((row, index) => (
                                        <tr key={row.Transaction_id || index} className="text-gray-700 hover:bg-gray-50/50">
                                            {columns.map((col) => {
                                                const rawValue = row[col.key];
                                                const displayValue = col.render
                                                    ? col.render(rawValue, row)
                                                    : (rawValue ?? "-");

                                                return (
                                                    <td
                                                        key={col.key}
                                                        className={`px-4 py-3 whitespace-nowrap ${col.align === "right" ? "text-right" : "text-left"
                                                            }`}
                                                    >
                                                        {displayValue}
                                                    </td>
                                                );
                                            })}
                                        </tr>
                                    ))
                                )}
                            </tbody>
                        </table>
                    </div>
                )}

                <div className="flex items-center justify-between mt-4">
                    <p className="text-xs text-gray-400">
                        {totalRows.toLocaleString()} record {totalRows === 1 ? "" : "s"}
                    </p>
                    <div className="flex items-center gap-2">
                        <button
                            onClick={() => setPage((p) => Math.max(1, p - 1))}
                            disabled={page <= 1}
                            className={`px-3 py-1 rounded text-[12px] border transition-all ${page <= 1
                                ? "border-gray-200 text-gray-300 cursor-not-allowed"
                                : "border-[#00305E] text-[#00305E] hover:bg-blue-50"
                                }`}
                        >
                            Previous
                        </button>
                        <span className="text-[12px] text-gray-500">
                            Page {page} of {totalPages}
                        </span>
                        <button
                            onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                            disabled={page >= totalPages}
                            className={`px-3 py-1 rounded text-[12px] border transition-all ${page >= totalPages
                                ? "border-gray-200 text-gray-300 cursor-not-allowed"
                                : "border-[#00305E] text-[#00305E] hover:bg-blue-50"
                                }`}
                        >
                            Next
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
}